import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NbGlobalLogicalPosition, NbToastrService } from '@nebular/theme';
import { NotificacaoDataService } from './service/notificacao-data.service';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, finalize, map, switchMap, timeout } from 'rxjs/operators';
import { Notificacao } from './models/interfaces/Notificacao';
import { NbAuthService, NbAuthJWTToken } from '@nebular/auth';
import { Router } from '@angular/router';
import { NegocioDataService } from '../../../pages/negocio/shared/services/NegocioController/negocio-data.services';
import { ListaCliente } from '../../../shared/Operations/listaCliente';
import { RegistrarAcessoService } from '../../../shared/services/registrar-acesso';
import { environment } from '../../../../environments/environment';

const API_URL = environment.siteUrl;
const PRODUCAO = environment.production;

@Component({
  selector: 'ngx-notificacoes',
  templateUrl: './notificacoes.component.html',
  styleUrls: ['./notificacoes.component.scss']
})
export class NotificacoesComponent implements OnInit, OnDestroy {
  logicalPositions = NbGlobalLogicalPosition;
  private static readonly TAMANHO_LOTE_INFINITE_LIST = 10;
  private static readonly TIPO_SOLICITACAO_VALIDACAO_NEGOCIO_FECHADO = 14;

  /** Quando true (ex.: sino do header), carrega via `GetNotificacaoPendentes`; caso contrário, `GetNotificacao`. */
  @Input() usarEndpointPendentes = false;

  notificacoes: Notificacao[] = [];
  notificacoesPendentesVisiveis: Notificacao[] = [];
  notificacoesNaoLidas: number = 0;
  mostrarLista: boolean = false;
  isConnected: boolean = false;
  connectionState: string = 'Desconectado';
  isTentandoConectar: boolean = false;
  private primeiraAtualizacaoSignalR: boolean = true;
  clienteLinkCarregandoId: string | null = null;
  atualizandoNotificacoes = false;
  
  private subscriptions: Subscription[] = [];

  constructor(
    private toastrService: NbToastrService,
    private notificacaoDataService: NotificacaoDataService,
    private authService: NbAuthService,
    private router: Router,
    private negocioDataService: NegocioDataService,
    private registrarAcessoService: RegistrarAcessoService,
  ) {}

  get notificacoesPendentes(): Notificacao[] {
    return this.notificacoes.filter(n => !n.lida);
  }

  get podeCarregarMaisPendentes(): boolean {
    return this.notificacoesPendentesVisiveis.length < this.notificacoesPendentes.length;
  }

  ngOnInit(): void {
    this.setupSubscriptions();
    this.carregarNotificacoesIniciais();
    // Conecta apenas uma vez no início
    this.conectarSignalR();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.desconectarSignalR();
  }

  private setupSubscriptions(): void {

    // Escuta notificações do SignalR
    const notificacoesSub = this.notificacaoDataService.getNotificacoesEvent().subscribe(
      result => {
      // Guarda o estado anterior para saber o que chegou de novo
      const notificacoesAnteriores = [...this.notificacoes];

      const lista = Array.isArray(result) ? result : [];
      // SEMPRE substitui a lista completa, nunca adiciona (hub/API: camelCase ou PascalCase)
      this.notificacoes = this.deduplicarNotificacoes(lista.map(n => this.mapearItemNotificacao(n)));

      this.atualizarContador();
      this.resetarInfiniteListPendentes();

      // Exibe mensagem mais rica quando chegarem novas notificações via SignalR
      if (!this.primeiraAtualizacaoSignalR) {
        const novasNaoLidas = this.notificacoes.filter(n =>
          !n.lida && !notificacoesAnteriores.some(a => a.id === n.id)
        );

        if (novasNaoLidas.length > 0) {
          if (novasNaoLidas.length === 1) {
            const nova = novasNaoLidas[0];
            const corpo = nova.mensagem
              ? `${nova.titulo}: ${nova.mensagem}`
              : `${nova.titulo}`;

            this.toastrService.info(
              corpo,
              'Nova notificação',
              { position: this.logicalPositions.BOTTOM_END }
            );
          } else {
            this.toastrService.info(
              `Você recebeu ${novasNaoLidas.length} novas notificações. Ex: ${novasNaoLidas[0].titulo}.`,
              'Novas notificações',
              { position: this.logicalPositions.BOTTOM_END }
            );
          }
        }
      }

      this.primeiraAtualizacaoSignalR = false;
    });

    // Escuta estado da conexão
    const connectionSub = this.notificacaoDataService.getConnectionState().subscribe(state => {
      this.isConnected = this.notificacaoDataService.isConnected();
      this.connectionState = this.getConnectionStateText(state);
    });

    const novaNotificacaoSub = this.notificacaoDataService.getNovaNotificacaoEvent().subscribe(nova => {
      if (!nova || this.primeiraAtualizacaoSignalR) {
        return;
      }
      const mapeada = this.mapearItemNotificacao(nova);
      const descricaoTipo = `${mapeada.titulo} ${mapeada.mensagem}`.toLowerCase();
      if (descricaoTipo.includes('compartilh')) {
        this.toastrService.info(
          mapeada.mensagem || mapeada.titulo,
          'Negócio compartilhado com você',
          { position: this.logicalPositions.TOP_END },
        );
      }
    });

    this.subscriptions.push(notificacoesSub, connectionSub, novaNotificacaoSub); // SignalR reabilitado
  }

  /**
   * Limite de espera no carregamento inicial via HTTP (evita requisição pendurada para sempre).
   * Lista grande na API pode ultrapassar 5s com facilidade — valor maior + falha silenciosa;
   * o SignalR ainda pode preencher a lista depois.
   */
  private static readonly NOTIFICACOES_TIMEOUT_MS = 45000;

  private obterListaNotificacoes$(): Observable<any> {
    return this.usarEndpointPendentes
      ? this.notificacaoDataService.GetNotificacoesPendentes()
      : this.notificacaoDataService.GetNotificacoes();
  }

  /** Extrai array de notificações da resposta da API (data/Data; lista vazia se success false sem dados). */
  private extrairListaNotificacoesResposta(value: any): any[] {
    if (!value) {
      return [];
    }
    const arr = value.data ?? value.Data;
    if (Array.isArray(arr)) {
      return arr;
    }
    return [];
  }

  /** Mapeia um item vindo da API ou do SignalR (PascalCase / camelCase / modelo simplificado do hub). */
  private mapearItemNotificacao(n: any): Notificacao {
    const id = String(n?.Id ?? n?.id ?? '');
    const tipo = n?.TipoNotificacao ?? n?.tipoNotificacao;
    const codigo = Number(tipo?.Codigo ?? tipo?.codigo ?? 0);
    const descricaoTipo = String(tipo?.Descricao ?? tipo?.descricao ?? 'Sem descrição').trim();
    const ehSolicitacaoValidacaoNegocioFechado =
      codigo === NotificacoesComponent.TIPO_SOLICITACAO_VALIDACAO_NEGOCIO_FECHADO;
    const tituloDireto = String(n?.Titulo ?? n?.titulo ?? '').trim();
    const titulo = tituloDireto || this.gerarTituloNotificacao(descricaoTipo || 'Sem descrição');
    const mensagemDireta = String(n?.Mensagem ?? n?.mensagem ?? '').trim();
    const mensagem = mensagemDireta || this.gerarMensagemNotificacao(n);
    const dataHoraRaw = n?.createdDate ?? n?.CreatedDate ?? n?.dataHora ?? n?.DataHora ?? '';
    const dataHora = dataHoraRaw != null && dataHoraRaw !== '' ? String(dataHoraRaw) : '';
    const lida = !!(n?.Lido ?? n?.lido);
    const tipoVisual = this.mapearTipoNotificacaoDeItem(n, codigo);

    const usuarioSolicitante = String(
      n?.UsuarioNotificacao?.employeeName ?? n?.usuarioNotificacao?.employeeName ?? 'Sistema'
    );
    const clienteIdRaw = n?.Cliente?.id ?? n?.cliente?.id ?? n?.client_id ?? n?.cliente_Id ?? n?.Cliente_Id ?? '';
    const clienteId = clienteIdRaw != null && String(clienteIdRaw).trim() !== '' ? String(clienteIdRaw) : undefined;
    return {
      id,
      titulo,
      mensagem,
      observacao: String(n?.Observacao ?? n?.observacao ?? ''),
      clienteNome: String(n?.Cliente?.name ?? n?.Cliente?.nomeCliente ?? n?.cliente?.name ?? n?.cliente?.nomeCliente ?? ''),
      clienteId,
      tipo: tipoVisual,
      dataHora,
      lida,
      usuarioNome: usuarioSolicitante,
      responsavelNotificacao: usuarioSolicitante,
      detalheUsuarioLabel: ehSolicitacaoValidacaoNegocioFechado ? 'Solicitante' : 'Responsável'
    } as Notificacao;
  }

  private mapearTipoNotificacaoDeItem(n: any, codigo: number): 'info' | 'success' | 'warning' | 'danger' {
    const t = String(n?.Tipo ?? n?.tipo ?? '').toLowerCase();
    if (t === 'info' || t === 'success' || t === 'warning' || t === 'danger') {
      return t;
    }
    return this.mapearTipoNotificacao(codigo);
  }

  private async carregarNotificacoesIniciais(): Promise<void> {
    try {
      const response = await this.obterListaNotificacoes$()
        .pipe(timeout(NotificacoesComponent.NOTIFICACOES_TIMEOUT_MS))
        .toPromise();
      const lista = this.extrairListaNotificacoesResposta(response);
      this.notificacoes = this.deduplicarNotificacoes(lista.map(item => this.mapearItemNotificacao(item)));
      this.atualizarContador();
      this.resetarInfiniteListPendentes();
    } catch (error: any) {
      // Sem toast: lista grande ou rede lenta não deve incomodar o usuário; SignalR pode atualizar em seguida.
      const timeout =
        error?.name === 'TimeoutError' ||
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('timeout'));
      if (timeout) {
        console.warn('[Notificações] Carregamento inicial excedeu o tempo limite; aguardando SignalR ou nova tentativa ao abrir o sino.');
      } else {
        console.warn('[Notificações] Falha no carregamento inicial:', error);
      }
      this.notificacoes = [];
      this.atualizarContador();
    }
  }

  carregarNotificacoes(): void {
    this.atualizandoNotificacoes = true;

    this.obterListaNotificacoes$().pipe(
      finalize(() => {
        this.atualizandoNotificacoes = false;
      })
    ).subscribe(
      value => {
        const lista = this.extrairListaNotificacoesResposta(value);
        this.notificacoes = this.deduplicarNotificacoes(lista.map(item => this.mapearItemNotificacao(item)));
        this.atualizarContador();
        this.resetarInfiniteListPendentes();
      },
      err => {
        this.toastrService.danger(err.error?.Message || 'Erro ao carregar notificações', 'Erro!', { position: this.logicalPositions.TOP_END });
      }
    );
  }

  atualizarNotificacoes(event?: Event): void {
    event?.stopPropagation();

    if (this.atualizandoNotificacoes) {
      return;
    }

    this.carregarNotificacoes();
  }

  private async conectarSignalR(): Promise<void> {
    // Se já estiver conectado, não faz nada
    if (this.notificacaoDataService.isConnected()) {
      return;
    }

    try {
      const usuarioId = this.getUsuarioId();
      const token = await this.getTokenAsync();
      
      if (!usuarioId) {
        return;
      }

      await this.notificacaoDataService.conectarSignalR(usuarioId, token);
    } catch (error) { 
    }
  }

  private async desconectarSignalR(): Promise<void> {
    try {
      await this.notificacaoDataService.desconectarSignalR();
    } catch (error) {
      // Erro silencioso ao desconectar
    }
  }

  private getUsuarioId(): string {
    // Obtém o ID do usuário do sessionStorage (definido no header.component.ts)
    const usuarioId = sessionStorage.getItem('usuarioId');
    if (usuarioId) {
      return usuarioId;
    }
    
    // Fallback: tenta obter do token JWT
    let userId = null;
    this.authService.getToken().subscribe((token: NbAuthJWTToken) => {
      if (token && token.isValid()) {
        const payload = token.getPayload();
        userId = payload?.usuarioId;
      }
    });
    
    return userId || '';
  }

  private getToken(): string | undefined {
    let tokenValue = undefined;
    this.authService.getToken().subscribe((token: NbAuthJWTToken) => {
      if (token && token.isValid()) {
        tokenValue = token.getValue();
      }
    });
    return tokenValue;
  }

  private async getTokenAsync(): Promise<string | undefined> {
    return new Promise((resolve) => {
      this.authService.getToken().subscribe((token: NbAuthJWTToken) => {
        if (token && token.isValid()) {
          resolve(token.getValue());
        } else {
          resolve(undefined);
        }
      });
    });
  }

  private getConnectionStateText(state: any): string {
    switch (state) {
      case 0: return 'Desconectado';
      case 1: return 'Conectando';
      case 2: return 'Conectado';
      case 3: return 'Reconectando';
      default: return 'Desconhecido';
    }
  }

  private mapearTipoNotificacao(codigo: number): 'info' | 'success' | 'warning' | 'danger' {
    
    switch (codigo) {
      case 1: return 'info';
      case 2: return 'success';
      case 3: return 'warning';
      case 4: return 'danger';
      default: return 'info';
    }
  }

  atualizarContador(): void {
    const naoLidas = this.notificacoes.filter(n => !n.lida).length;
    this.notificacoesNaoLidas = naoLidas;
  }

  toggleLista(): void {
    this.mostrarLista = !this.mostrarLista;
    if (this.mostrarLista) {
      this.resetarInfiniteListPendentes();
    }
  }

  marcarComoLida(notificacao: Notificacao): void {
    this.notificacaoDataService.MarcarNotificacaoComoLida(notificacao.id).subscribe( 
      value => {
        if (value.Success) {
          const msg = 'Notificação marcada como lida com sucesso!';
          
          this.toastrService.success(msg, 'Sucesso!', { position: this.logicalPositions.TOP_END });
          this.carregarNotificacoes();
        } else {
          this.toastrService.danger(value.message || 'Erro ao marcar notificação como lida', 'Erro!', { position: this.logicalPositions.TOP_END });
        }
      },
      err => {
        this.toastrService.danger(err.error?.Message || 'Erro ao marcar notificação como lida', 'Erro!', { position: this.logicalPositions.TOP_END });
      }
    );
  }

  marcarTodasComoLidas(): void {
    const notificacoesNaoLidas = this.notificacoes.filter(n => !n.lida);
    
    if (notificacoesNaoLidas.length === 0) {
      this.toastrService.info('Não há notificações para marcar como lidas', 'Info', {
        position: this.logicalPositions.TOP_END
      });
      return;
    }

    // Marca a primeira notificação e recarrega a lista
    // O recarregamento irá atualizar todas as notificações
    this.notificacaoDataService.MarcarTodasNotificacoesComoLida().subscribe(
      value => {
        if (value.Success) {
          const msg = 'Todas as notificações foram marcadas como lidas com sucesso!';
          
          this.toastrService.success(msg, 'Sucesso!', { position: this.logicalPositions.TOP_END });
          this.carregarNotificacoes();
        } else {
          this.toastrService.danger(value.message || 'Erro ao marcar notificações como lidas', 'Erro!', { position: this.logicalPositions.TOP_END });
        }
      },
      err => {
        this.toastrService.danger(err.error?.Message || 'Erro ao marcar notificações como lidas', 'Erro!', { position: this.logicalPositions.TOP_END });
      }
    );
  }

  abrirNotificacaoAdmin(): void {
    this.mostrarLista = false;
    this.router.navigate(['/pages/admin/notificacao-admin']);
  }

  abrirCriarNotificacao(): void {
    this.mostrarLista = false;
    this.router.navigate(['/pages/admin/notificacao-admin'], {
      queryParams: { aba: 'criar' }
    });
  }

  abrirCliente(event: Event, notificacao: Notificacao): void {
    event.preventDefault();
    event.stopPropagation();

    if (!notificacao.clienteId || this.clienteLinkCarregandoId) {
      return;
    }

    const urlCliente = PRODUCAO
      ? `${API_URL}/pages/negocio/cliente/editar?aba=1`
      : 'http://localhost:4200/pages/negocio/cliente/editar?aba=1';

    const novaAba = window.open('', '_blank');
    if (!novaAba) {
      this.toastrService.warning(
        'Permita pop-ups para abrir o cliente em nova aba.',
        'Atenção',
        { position: this.logicalPositions.TOP_END }
      );
      return;
    }

    this.clienteLinkCarregandoId = notificacao.id;

    this.buscarNegocioDoCliente(notificacao.clienteId).subscribe(
      negocio => {
        this.clienteLinkCarregandoId = null;

        if (!negocio) {
          novaAba.close();
          this.toastrService.warning('Não retornou dados', 'Atenção', { position: this.logicalPositions.TOP_END });
          return;
        }

        this.abrirClienteComNegocio(novaAba, urlCliente, negocio);
      },
      () => {
        this.clienteLinkCarregandoId = null;
        novaAba.close();
        this.toastrService.danger('Erro ao carregar dados do cliente', 'Erro', { position: this.logicalPositions.TOP_END });
      }
    );
  }

  podeAbrirLinkCliente(notificacao: Notificacao): boolean {
    return !!notificacao.clienteId && this.clienteLinkCarregandoId !== notificacao.id;
  }

  isClienteLinkCarregando(notificacao: Notificacao): boolean {
    return this.clienteLinkCarregandoId === notificacao.id;
  }

  getTooltipLinkCliente(notificacao: Notificacao): string {
    if (this.isClienteLinkCarregando(notificacao)) {
      return 'Carregando dados do cliente...';
    }
    return 'Abrir detalhe do cliente';
  }

  private buscarNegocioDoCliente(clienteId: string): Observable<any> {
    return this.negocioDataService.CarregarNegociosCliente(clienteId).pipe(
      switchMap(value => {
        const negocios = value?.data ?? value?.Data;
        const success = value?.success ?? value?.Success;

        if (success && Array.isArray(negocios) && negocios.length > 0) {
          return of(negocios[0]);
        }

        return this.negocioDataService.GetPrimeiroNegocioDoClient(clienteId).pipe(
          map(result => {
            const negocio = result?.data ?? result?.Data;
            const ok = result?.success ?? result?.Success;
            return ok && negocio ? negocio : null;
          })
        );
      }),
      catchError(() =>
        this.negocioDataService.GetPrimeiroNegocioDoClient(clienteId).pipe(
          map(result => {
            const negocio = result?.data ?? result?.Data;
            const ok = result?.success ?? result?.Success;
            return ok && negocio ? negocio : null;
          }),
          catchError(() => of(null))
        )
      )
    );
  }

  private persistirClienteParaNovaAba(novaAba: Window, client: ListaCliente): void {
    const payload = JSON.stringify(client);

    sessionStorage.removeItem('cliente');
    sessionStorage.setItem('cliente', payload);

    try {
      if (!novaAba.closed) {
        novaAba.sessionStorage.removeItem('cliente');
        novaAba.sessionStorage.setItem('cliente', payload);
      }
    } catch {
      // Ignora se o navegador bloquear acesso temporário à nova aba.
    }
  }

  private abrirClienteComNegocio(novaAba: Window, urlCliente: string, negocio: any): void {
    const client = this.mapearClienteNegocio(negocio);

    this.persistirClienteParaNovaAba(novaAba, client);
    this.registrarAcessoService.registerUserIP(client);

    if (!novaAba.closed) {
      novaAba.location.href = urlCliente;
    }
  }

  private mapearClienteNegocio(negocio: any): ListaCliente {
    return {
      idNegocio: negocio.id,
      idCliente: negocio.Client.id,
      nomeCliente: negocio.Client.name,
      cpfCnpj: negocio.Client.document_Number,
      analista: negocio.Employee.employeeName,
      dadosUsuario: negocio.Employee,
      dataNascimento: negocio.Client.birthDate != null ? negocio.Client.birthDate : null,
      profissao: negocio.Client.profession != null ? negocio.Client.profession : '',
      endereco: negocio.Client.address != null ? negocio.Client.address : '',
      cidade: negocio.Client.city != null ? negocio.Client.city : '',
      estado: negocio.Client.state != null ? negocio.Client.state : '',
      dataObito: negocio.Client.deathDate != null ? negocio.Client.deathDate : null,
      cadastro: negocio.company != null ? negocio.company : '',
      situacaoCpfCnpj: negocio.Client.ClientSituation != null && negocio.Client.ClientSituation.code != null ? negocio.Client.ClientSituation.code.toString() : null,
      dadoObito: negocio.Client.deathData != null ? negocio.Client.deathData : '',
      negocio,
      situacaoNegocio: '',
      analistaFeedback: '',
      dtContato: null,
      dtRetorno: null,
      retornarEm: null,
      obito: negocio.Client.obito,
      feedback: null,
      analistaCompartilhado: negocio.AnalistaCompartilhado,
      valorAtual: null,
      dtOutroRetorno: null,
      dataNegocioFechado: null,
      idAnalista: negocio.Employee.Id,
      porcentagemReno: negocio.closedPercentage != null ? negocio.closedPercentage : null,
      valorPrevisaoReno: negocio.valorPrevisaoReno != null ? negocio.valorPrevisaoReno : null,
      temHerdeiro: negocio.TemHerdeiro,
      tipoConhecimentoAtivo: negocio.TipoConhecimentoAtivos != null ? negocio.TipoConhecimentoAtivos.codigo : null,
      valorSaldo: negocio.ValorSaldo != null ? negocio.ValorSaldo : null,
      valorAtualNegocio: (negocio.valorAtual != null ? negocio.valorAtual : 0) + (negocio.ValorSaldo != null ? negocio.ValorSaldo : 0),
      temHerdeiroComPagamento: negocio.PagamentoEfetuado != null ? negocio.PagamentoEfetuado : false,
      faseNegocio: negocio.FaseNegocio != null ? negocio.FaseNegocio : null,
      previsaoCaixa: null
    };
  }

  carregarMaisPendentes(): void {
    if (!this.podeCarregarMaisPendentes) {
      return;
    }

    const inicio = this.notificacoesPendentesVisiveis.length;
    const fim = inicio + NotificacoesComponent.TAMANHO_LOTE_INFINITE_LIST;
    const proximas = this.notificacoesPendentes.slice(inicio, fim);
    this.notificacoesPendentesVisiveis = [...this.notificacoesPendentesVisiveis, ...proximas];
  }


  formatarTempo(dataHora: string): string {
    const data = new Date(dataHora);
    const agora = new Date();
    const diffMs = agora.getTime() - data.getTime();
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMin < 60) {
      return `${diffMin}min atrás`;
    } else if (diffHoras < 24) {
      return `${diffHoras}h atrás`;
    } else {
      return `${diffDias}d atrás`;
    }
  }

  obterClasseTipo(tipo: string): string {
    const classes = {
      'info': 'notificacao-info',
      'success': 'notificacao-success',
      'warning': 'notificacao-warning',
      'danger': 'notificacao-danger'
    };
    return classes[tipo] || 'notificacao-info';
  }

  private gerarTituloNotificacao(tipoDescricao: string): string {
    // Remove redundância e torna mais claro
    const titulos = {
      'Modificar Requisição': 'Nova Solicitação de Documento',
      'Aprovar Requisição': 'Solicitação Aprovada',
      'Rejeitar Requisição': 'Solicitação Rejeitada',
      'Documento Entregue': 'Documento Entregue',
      'Solicitação Cancelada': 'Solicitação Cancelada'
    };
    
    return titulos[tipoDescricao] || tipoDescricao;
  }

  private gerarMensagemNotificacao(notificacao: any): string {
    const clienteNome = notificacao.cliente?.name || notificacao.Cliente?.name || 
                       notificacao.cliente?.nomeCliente || notificacao.Cliente?.nomeCliente || '';
    const responsavel = notificacao.usuarioNotificacao?.employeeName ||
                       notificacao.UsuarioNotificacao?.employeeName || 'Sistema';
    
    if (clienteNome) {
      return `Cliente: ${clienteNome}`;
    }
    
    return `Enviado por: ${responsavel}`;
  }

  private deduplicarNotificacoes(notificacoes: Notificacao[]): Notificacao[] {
    const ids = new Set<string>();
    return notificacoes.filter(notificacao => {
      if (!notificacao?.id) {
        return true;
      }
      if (ids.has(notificacao.id)) {
        return false;
      }
      ids.add(notificacao.id);
      return true;
    });
  }

  obterTipoBadge(tipo: string): string {
    const badges = {
      'info': 'Info',
      'success': 'Sucesso',
      'warning': 'Atenção',
      'danger': 'Urgente'
    };
    return badges[tipo] || 'Info';
  }

  private resetarInfiniteListPendentes(): void {
    this.notificacoesPendentesVisiveis = [];
    this.carregarMaisPendentes();
  }
}
